import { AppointmentDetailsLocale } from '../interfaces';
import { GetPatientOutput } from 'graphql/generated/hasura';
import { Link } from 'react-router-dom';
import React, { useMemo } from 'react';
import {
  extractUUIDFromResURL,
  formatDateToCustomFormat,
} from 'utilities/functions';
import { useGetSignedUrlFromStorage } from 'hooks/useGetSignedUrlFromStorage';
import { Card } from '../../../app/my-appointments/components/Card';
import Avatar from '../../avatarComponent';
import { MY_PATIENTS } from 'utilities/routes';

interface IPatientInfo {
  locale: AppointmentDetailsLocale;
  patient: GetPatientOutput;
}

export function PatientInfo({ locale, patient }: IPatientInfo) {
  const patientBigField = useMemo(
    () => [
      {
        label: locale.dateOfBirth,
        value: formatDateToCustomFormat(patient?.SENSITIVE_dob ?? '', true),
      },
      {
        label: locale.ethnicity,
        value: (patient?.SENSITIVE_etnicity ?? []).join(', '),
      },
    ],
    [locale, patient],
  );

  const patientField = useMemo(
    () => [
      {
        label: locale.birthGender,
        value: patient?.SENSITIVE_gender,
      },
      {
        label: locale.selfIdentification,
        value: patient?.SENSITIVE_self_identity_gender,
      },
      {
        label: locale.height,
        value: `${patient?.SENSITIVE_user_measurements?.heightFt} ft. ${patient?.SENSITIVE_user_measurements?.heightIn} in.`,
      },
      {
        label: locale.weight,
        value: `${patient?.SENSITIVE_user_measurements?.weight} lbs.`,
      },
      {
        label: locale.waist,
        value: `${patient?.SENSITIVE_user_measurements?.waist} in.`,
      },
      {
        label: locale.hips,
        value: `${patient?.SENSITIVE_user_measurements?.hips} in.`,
      },
    ],
    [locale, patient],
  );

  const patientFullName = `${patient?.SENSITIVE_firstname || ''} ${
    patient?.SENSITIVE_lastname || ''
  }`;

  const photoUrl = extractUUIDFromResURL(
    patient.SENSITIVE_profile_picture_id || '',
  );
  const signedProfileUrl = useGetSignedUrlFromStorage(photoUrl, patient.id);

  return (
    <Card
      title={locale.patientInfo || 'Patient info'}
      titleClassName="flex text-h4 text-btn-black font-semibold"
      alwaysExpanded
      showSeparator
      rightComponentWrap={false}
      rightComponent={
        <Link
          className="text-clc-blue text-sm uppercase font-bold underline"
          target="_blank"
          to={`${MY_PATIENTS}/${patient?.id}`}
          state={{ patientId: patient?.id }}
          data-testid="go-to-patient-bio-info"
        >
          {locale.profile}
        </Link>
      }
      testId={'patient-info'}
    >
      <div className="flex w-full flex-col gap-2.5">
        <Avatar
          size="sm"
          key={signedProfileUrl}
          imageUrl={signedProfileUrl}
          customClasses="h-16 w-16 rounded-full mr-2"
        />
        <div className="font-semibold text-xl">{patientFullName}</div>
      </div>

      <div className="grid gap-x-2.5 gap-y-30 grid-cols-2 w-full">
        {patientBigField.map(({ label, value }) => (
          <div className="flex flex-col col-span-2 gap-2.5 w-full">
            <div className="font-semibold">{label}</div>
            <div className="text-med-gray-3">{value}</div>
          </div>
        ))}

        {patientField.map(({ label, value }) => (
          <div className="flex flex-col gap-2.5 w-full">
            <div className="font-semibold">{label}</div>
            <div className="text-med-gray-3">{value}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
